/* ═══════════════════════════════════════════════════════════════
   ExpenseService — Expense reports CRUD via EC2 API (cloud, in-memory cache)
   ═══════════════════════════════════════════════════════════════ */

import type { Expense, ExpenseAttachment } from '../../shared/types';
import { getCurrentFirmId } from './firmContext';
import * as cache from './cloudCache';
import * as cloudApi from './apiClient';

// Captured at module load — works only because main.tsx imports webApiShim
// before any service module, so window.api already exists (import-order contract).
const api = (window as any).api;

export const ExpenseService = {
  list: async (memberId?: string, month?: string, status?: string): Promise<Expense[]> => {
    const filters: Record<string, any> = {};
    if (memberId) filters.teamMemberId = memberId;
    if (month) filters.month = month;
    if (status) filters.status = status;
    if (Object.keys(filters).length > 0) {
      return cache.query<Expense>('expenses', filters);
    }
    return cache.list<Expense>('expenses');
  },

  save: async (e: Expense): Promise<Expense> => cache.save('expenses', e),

  delete: async (id: string): Promise<void> => cache.remove('expenses', id),

  updateStatus: async (ids: string[], status: string, performedBy?: string): Promise<void> => {
    const firmId = getCurrentFirmId();
    const now = new Date().toISOString();
    const updates: Record<string, any> = { status, updated_at: now };
    if (status === 'submitted') updates.submitted_at = now;
    if (status === 'approved' && performedBy) {
      updates.approved_by = performedBy;
      updates.approved_at = now;
    }

    await cloudApi.dbUpdate('expenses', updates, [
      { col: 'firm_id', op: '=', val: firmId },
      { col: 'id', op: 'IN', val: ids },
    ]);
    cache.invalidate('expenses');
  },

  // Attachments
  listAttachments: async (expenseId: string): Promise<ExpenseAttachment[]> => {
    return cache.query<ExpenseAttachment>('expense_attachments', { expenseId });
  },

  saveAttachment: async (att: ExpenseAttachment): Promise<ExpenseAttachment> => {
    return cache.save('expense_attachments', att);
  },

  deleteAttachment: async (id: string): Promise<void> => cache.remove('expense_attachments', id),

  // File dialog — stays as IPC (needs main process for native dialog)
  openFileDialog: async (): Promise<{
    data: string;
    fileName: string;
    mimeType: string;
  } | null> => {
    return api.openExpenseFileDialog();
  },
};
